import type { FilterMenuConfig, FilterOption } from "@/shared/models/filterTypes";
import type { Portfolio } from "@/types/Portfolio";
import type { TagsWatchlist } from "@/shared/api/pref-storage/tags-watchlists";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import { menuCategories } from "@/widget-components/newsquawk-headlines/models/categories";
import { formatListName } from "@/shared/utils/formatListName";
import { savedViewColor } from "@/shared/utils/savedViewHighlight";
import { getPrebuiltCombinedColorsSnapshot } from "@/pages/PortfolioBuilder/prebuiltCombinedOverrides";
import type { LibrarySection, LibraryCategory, LibraryItem } from "./LibraryBrowserContext";

// ── FilterMenuConfig → LibrarySection[] ─────────────────────────────────
// Used by the filter modal to convert existing widget filter configs
// into the unified library format.

// Label cleanup: "Filter by Ticker Watchlists" → type based on subsection
const SUB_LABELS: Record<string, { custom: string; newsquawk: string }> = {
  "ticker-watchlists": { custom: "Ticker Watchlists", newsquawk: "Newsquawk Tickers" },
  "keyword-watchlists": { custom: "Keyword Watchlists", newsquawk: "Newsquawk Keywords" },
  "tag-watchlists": { custom: "Tag Watchlists", newsquawk: "Newsquawk Tags" },
  "category": { custom: "Combined watchlists", newsquawk: "Newsquawk combined watchlists" },
};

function subsectionLabel(configId: string, subId: string, fallbackLabel: string): string {
  // Strip -filter / -highlight suffix to get the base type
  const baseId = configId.replace(/-filter$/, "").replace(/-highlight$/, "");
  const labels = SUB_LABELS[baseId];
  if (!labels) return fallbackLabel;
  if (subId.startsWith("user-") || subId.startsWith("your-")) return labels.custom;
  if (subId.startsWith("newsquawk-")) return labels.newsquawk;
  return fallbackLabel;
}

export interface FilterCreateCallbacks {
  onCreateTicker?: () => void;
  onCreateKeyword?: () => void;
  onCreateTag?: () => void;
  onCreateAdvanced?: () => void;
}

export function filterConfigToLibrarySections(
  configs: FilterMenuConfig[],
  portfolios?: Portfolio[],
  createCallbacks?: FilterCreateCallbacks
): LibrarySection[] {
  // Build a lookup for enriching items with portfolio metadata
  const portfolioMap = new Map<string, Portfolio>();
  if (portfolios) {
    for (const p of portfolios) {
      portfolioMap.set(String(p.id), p);
    }
  }
  // Map subsection IDs to create callbacks
  const callbackForSub = (subId: string): (() => void) | undefined => {
    if (!createCallbacks) return undefined;
    if (subId.startsWith("user-tickers") || subId.startsWith("your-tickers")) return createCallbacks.onCreateTicker;
    if (subId.startsWith("user-keywords") || subId.startsWith("your-keywords")) return createCallbacks.onCreateKeyword;
    if (subId.startsWith("user-tags") || subId.startsWith("your-tags")) return createCallbacks.onCreateTag;
    if (subId.startsWith("user-filters") || subId.startsWith("your-filters")) return createCallbacks.onCreateAdvanced;
    return undefined;
  };

  const sections: LibrarySection[] = [];

  for (const config of configs) {
    if (config.subsections && config.subsections.length > 0) {
      // Flatten: each subsection becomes its own top-level section
      for (const sub of config.subsections) {
        const subItems = sub.filterIds
          .map((id) => config.filterOptions.find((opt) => opt.id === id))
          .filter((opt): opt is FilterOption => !!opt && !opt.isHeader);

        if (subItems.length === 0 && !sub.createNewLink) continue;

        const categories = extractCategoriesFromOptions(config.filterOptions, sub.filterIds);
        const label = subsectionLabel(config.id, sub.id, sub.label);

        sections.push({
          id: `${config.id}--${sub.id}`,
          label,
          groups: [{
            id: sub.id,
            label,
            items: subItems.map((opt) => enrichItem({
              id: String(opt.id),
              label: opt.label,
              disabled: opt.disabled ?? config.disabled,
            }, portfolioMap, config.id)),
            categories: categories.length > 0 ? categories : undefined,
            createAction: (() => {
              const cb = callbackForSub(sub.id);
              if (cb) return { label: sub.createNewLink?.label ?? "Create new...", onClick: cb };
              if (sub.createNewLink) return { label: sub.createNewLink.label ?? "Create new...", onClick: () => { window.location.href = sub.createNewLink!.url; } };
              return undefined;
            })(),
          }],
        });
      }
    } else {
      // No subsections — single flat section
      const items = config.filterOptions
        .filter((opt) => !opt.isHeader)
        .map((opt) => enrichItem({
          id: String(opt.id),
          label: opt.label,
          disabled: opt.disabled ?? config.disabled,
        }, portfolioMap, config.id));

      const categories = extractCategoriesFromOptions(config.filterOptions, config.filterOptions.map((o) => o.id));

      // Clean up label
      let label = config.label;
      if (label.startsWith("Filter by ")) label = label.replace("Filter by ", "");
      if (label.startsWith("Highlight by ")) label = label.replace("Highlight by ", "");

      sections.push({
        id: config.id,
        label,
        groups: [{
          id: config.id,
          label,
          items,
          categories: categories.length > 0 ? categories : undefined,
          createAction: (() => {
            // Check for callback based on config ID
            const cb = config.id.includes("category") ? createCallbacks?.onCreateAdvanced : undefined;
            if (cb) return { label: config.createNewLink?.label ?? "Create new...", onClick: cb };
            if (config.createNewLink) return { label: config.createNewLink.label ?? "Create new...", onClick: () => { window.location.href = config.createNewLink!.url; } };
            return undefined;
          })(),
        }],
      });
    }
  }

  // Reorder: custom first, then newsquawk (as reference), then other (Asset Class, Advanced Filters, etc.)
  const isCustom = (s: LibrarySection) => s.id.includes("user-") || s.id.startsWith("my-");
  const isNewsquawk = (s: LibrarySection) => s.id.includes("newsquawk-") || s.id.startsWith("nq-");

  const custom = sections.filter(isCustom);
  const newsquawk = sections
    .filter(isNewsquawk)
    .map((s) => ({ ...s, sectionVariant: "reference" as const }));
  const other = sections.filter((s) => !isCustom(s) && !isNewsquawk(s));

  return [...custom, ...newsquawk, ...other];
}

/** Enrich a library item with portfolio or tag metadata */
function enrichItem(item: LibraryItem, portfolioMap: Map<string, Portfolio>, configId?: string): LibraryItem {
  // Try portfolio match first
  const portfolio = portfolioMap.get(item.id);
  if (portfolio) {
    const isKeywordOnly = (portfolio.keywords?.length ?? 0) > 0 && portfolio.tickers.length === 0;
    return {
      ...item,
      color: portfolio.color,
      metadata: {
        ownerType: portfolio.owner_type,
        description: portfolio.description,
        itemType: isKeywordOnly ? "keyword" : "ticker",
        tickers: portfolio.tickers,
        keywords: portfolio.keywords ?? [],
        priority: portfolio.priority,
        notifications: portfolio.notifications,
        portfolioId: portfolio.id,
      },
    };
  }

  // Tag items — detect from config ID
  if (configId && configId.includes("tag-watchlists")) {
    return {
      ...item,
      metadata: {
        itemType: "tag",
        tags: [{ id: item.id, name: item.label }],
      },
    };
  }

  // Asset class / other items — add label as metadata for display
  if (configId && (configId.includes("basic-filter") || configId.includes("category-filter"))) {
    return {
      ...item,
      metadata: {
        itemType: configId.includes("basic-filter") ? "asset-class" : "advanced-filter",
      },
    };
  }

  return item;
}

/** Extract category boundaries from isHeader items in the options list */
function extractCategoriesFromOptions(
  allOptions: FilterOption[],
  scopedIds: (string | number)[]
): LibraryCategory[] {
  const scopedOptions = scopedIds
    .map((id) => allOptions.find((opt) => opt.id === id))
    .filter((opt): opt is FilterOption => !!opt);

  const categories: LibraryCategory[] = [];
  let currentCategory: LibraryCategory | null = null;

  for (const opt of scopedOptions) {
    if (opt.isHeader) {
      currentCategory = { id: String(opt.id), label: opt.label, itemIds: [] };
      categories.push(currentCategory);
    } else if (currentCategory) {
      currentCategory.itemIds.push(String(opt.id));
    }
  }

  return categories;
}

// ── Portfolio/Tag/Query → LibrarySection[] ──────────────────────────────
// Used by the Watchlist Builder page.

export function getWatchlistType(p: Portfolio): "ticker-only" | "keyword-only" | "mixed" {
  const hasTickers = p.tickers.length > 0;
  const hasKeywords = (p.keywords?.length ?? 0) > 0;
  if (hasTickers && hasKeywords) return "mixed";
  if (hasKeywords) return "keyword-only";
  return "ticker-only";
}

export function portfolioToLibrarySections(
  portfolios: Portfolio[],
  tagWatchlists: TagsWatchlist[],
  advancedFilters: CategoryQuery[],
  options?: {
    tagIdToName?: Record<string, string>;
    /**
     * Individual tags to list alongside the curated menu ones.
     *
     * The Newsquawk library's single-tag rows come from `menuCategories`, which
     * is 51 hand-picked tags. A selection pointing anywhere else in the
     * taxonomy — as a concierge-built filter routinely does — had no row to
     * resolve against, so Active counted it and then showed nothing. Hosts pass
     * the off-menu tags they hold selected; ids already in the menu are ignored.
     */
    extraTags?: { id: string; name: string }[];
    onCreateTicker?: () => void;
    onCreateKeyword?: () => void;
    onCreateTag?: () => void;
    onCreateAdvanced?: () => void;
  }
): LibrarySection[] {
  // One entry per watchlist, not one per type it happens to contain.
  //
  // These used to be two overlapping filters — `!== "keyword-only"` and
  // `!== "ticker-only"` — so a list holding both tickers AND keywords landed in
  // both. That was invisible while they were separate sections; in one list it
  // showed the same watchlist twice, under a duplicated React key, which is
  // also why one copy jumped out of alphabetical order.
  const itemTypeFor = (p: Portfolio): "ticker" | "keyword" =>
    getWatchlistType(p) === "keyword-only" ? "keyword" : "ticker";

  const userPortfolios = portfolios.filter((p) => p.owner_type === "USER");
  const newsquawkPortfolios = portfolios.filter((p) => p.owner_type === "NEWSQUAWK");

  const userTags = tagWatchlists.filter((tw) => tw.owner_type === "USER");
  const newsquawkTags = tagWatchlists.filter((tw) => tw.owner_type !== "USER");
  const userFilters = advancedFilters.filter((f) => f.owner_type === "USER");
  const newsquawkFilters = advancedFilters.filter((f) => f.owner_type === "NEWSQUAWK");

  const sections: LibrarySection[] = [];

  // ════════════════════════════════════════════════════════════
  // Custom sections first
  // ════════════════════════════════════════════════════════════

  // One list, not four.
  //
  // Ticker / keyword / tag are how a watchlist is *stored*, not a thing the
  // reader is looking for — nobody arrives wanting "my keyword watchlists".
  // Splitting the nav by our own type system made the user classify a list
  // before they could find it, and made the same word ("watchlist") appear
  // four times with four different qualifiers. Sorted by name so the merge
  // reads as one library rather than four piles concatenated.
  const collator = new Intl.Collator(undefined, { sensitivity: "base", numeric: true });
  const userItems: LibraryItem[] = [
    ...userPortfolios.map((p) => portfolioToItem(p, itemTypeFor(p))),
    ...userTags.map((tw) => tagWatchlistToItem(tw, options?.tagIdToName)),
    ...userFilters.map((f) => advancedFilterToItem(f, options?.tagIdToName)),
  ].sort((a, b) => collator.compare(a.label, b.label));

  // The builder no longer asks for a type up front, so one entry point serves
  // all of them; these handlers differ only in which one is wired by the host.
  const onCreate =
    options?.onCreateTicker ??
    options?.onCreateKeyword ??
    options?.onCreateTag ??
    options?.onCreateAdvanced;

  if (userItems.length > 0 || onCreate) {
    sections.push({
      id: "my-watchlists",
      label: "Your watchlists",
      groups: [{
        id: "user-watchlists",
        label: "Your watchlists",
        items: userItems,
        createAction: onCreate
          ? { label: "Create new...", onClick: onCreate }
          : undefined,
      }],
    });
  }

  // ════════════════════════════════════════════════════════════
  // Newsquawk watchlists — the same shape as yours
  // ════════════════════════════════════════════════════════════
  //
  // One flat list, no drawer and no per-type folders. Who wrote a watchlist
  // is worth knowing; how it happens to be stored is not, and it was the only
  // thing the Tickers / Keywords / Tags split ever told you. Search does the
  // narrowing that the folders were standing in for.

  const menuTagIds = new Set(
    menuCategories.flatMap((category) => category.items.map((i) => String(i.id)))
  );
  const tagItem = (id: string, name: string): LibraryItem => ({
    id,
    label: name,
    metadata: {
      itemType: "tag" as const,
      tags: [{ id, name }],
    },
  });

  const newsquawkItems: LibraryItem[] = [
    ...newsquawkPortfolios.map((p) => portfolioToItem(p, itemTypeFor(p))),
    ...newsquawkTags.map((tw) => tagWatchlistToItem(tw, options?.tagIdToName)),
    ...menuCategories.flatMap((category) =>
      category.items.map((catItem) => tagItem(String(catItem.id), catItem.title))
    ),
    ...(options?.extraTags ?? [])
      .filter((t) => !menuTagIds.has(t.id))
      .map((t) => tagItem(t.id, t.name)),
    ...newsquawkFilters.map((f) => advancedFilterToItem(f, options?.tagIdToName)),
  ].sort((a, b) => collator.compare(a.label, b.label));

  if (newsquawkItems.length > 0) {
    sections.push({
      id: "nq-library",
      label: "Newsquawk watchlists",
      groups: [{
        id: "newsquawk-watchlists",
        label: "Newsquawk watchlists",
        items: newsquawkItems,
      }],
    });
  }

  return sections;
}

function portfolioToItem(p: Portfolio, itemType: "ticker" | "keyword"): LibraryItem {
  return {
    id: String(p.id),
    label: formatListName(p.name),
    color: p.color,
    metadata: {
      ownerType: p.owner_type,
      description: p.description,
      itemType,
      tickers: p.tickers,
      keywords: p.keywords ?? [],
      priority: p.priority,
      notifications: p.notifications,
      portfolioId: p.id,
    },
  };
}

function tagWatchlistToItem(
  tw: TagsWatchlist,
  tagIdToName?: Record<string, string>
): LibraryItem {
  const tags = tw.tags.map((id) => ({
    id,
    name: tagIdToName?.[id] ?? `Tag #${id}`,
  }));

  return {
    id: `tag-${tw.id}`,
    label: formatListName(tw.name),
    metadata: {
      ownerType: tw.owner_type,
      description: tw.description,
      itemType: "tag",
      tags,
      notifications: tw.notifications,
      tagWatchlistId: tw.id,
    },
  };
}

function advancedFilterToItem(
  f: CategoryQuery,
  tagIdToName?: Record<string, string>
): LibraryItem {
  const filterPortfolios = (f.portfolios ?? []).map((p) => {
    const type = getWatchlistType(p);
    return {
      name: p.name,
      color: p.color,
      type: type === "keyword-only" ? "keywords" : "tickers",
      items: type === "keyword-only" ? (p.keywords ?? []) : p.tickers,
    };
  });

  const filterTags = (f.categories ?? []).map((id) => ({
    id,
    name: tagIdToName?.[id] ?? `Tag #${id}`,
  }));

  const filterTagsWatchlists = (f.tagsWatchlists ?? []).map((tw) => ({
    name: tw.name,
    tagCount: tw.tags.length,
    tags: tw.tags.map((id) => ({
      id,
      name: tagIdToName?.[id] ?? `Tag #${id}`,
    })),
  }));

  return {
    id: `filter-${f.id}`,
    label: formatListName(f.queryName),
    // The colour it highlights in — picked, or derived until someone picks one.
    color: savedViewColor(f),
    metadata: {
      ownerType: f.owner_type,
      // A local colour on a desk list, which "Reset" can take back off.
      hasOverride:
        f.owner_type === "NEWSQUAWK" &&
        f.id !== undefined &&
        f.id in getPrebuiltCombinedColorsSnapshot(),
      description: f.description,
      itemType: "advanced-filter",
      searchKeyword: f.searchKeyword,
      filterPortfolios,
      filterTags,
      filterTagsWatchlists,
      filterLogic: {
        filterMode: f.filterMode,
        keywordOp: f.keywordOp,
        keywordWatchlistOp: f.keywordWatchlistOp,
      },
      notifications: f.notifications,
      filterId: f.id,
    },
  };
}
